import { Link, Outlet, useParams, useSearchParams } from "react-router-dom";
import Breadcrumbs, { IBreadcrumbItem } from "../../../components/breadcrumb/breadcrumb";
import DropdownMenu from "components/dropdown-menu/dropdown-menu";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import clsx from "clsx";
import EmailCaseManagerModal from "components/emailCaseManagerModal/EmailCaseManagerModal";
import ICase from "@myinnsworth/models/case/ICase";
import { useAuthentication } from "@myinnsworth/hooks/authenticationHook";
import { useAppStore } from "@myinnsworth/hooks/appStoreHook";
import Icon from "components/icon/icon";
import FileUploader from "components/file-uploader/file-uploader";
import Modal from "components/modal/modal";
import FileUploadItem from "components/file-upload-item/file-upload-item";
import { applicantHttpService } from "@myinnsworth/httpServices";

export type CaseLayoutContext = {
    currentTab: string;
    totalType?: string;
};

export default function CaseLayout() {
    const appStore = useAppStore();
    const auth = useAuthentication();
    const params = useParams();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [files, setFiles] = useState<Array<File>>([]);
    const [uploading, setUploading] = useState<boolean>(false);
    const [allowUploadFiles, setAllowUploadFiles] = useState<boolean>(false);
    const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
    const [currentCase, setCurrentCase] = useState<ICase | undefined>(appStore.findCase(params.slug!));
    const [emailCaseManager, setEmailCaseManager] = useState<boolean>(false);
    const [breadcrumbItems, setBreadcrumbItems] = useState<Array<IBreadcrumbItem>>([]);
    const [currentTab, setCurrentTab] = useState<string>('news');
    const [currentTabName, setCurrentTabName] = useState<string>('News');
    const [totalType, setTotalType] = useState<string>('active');

    const removeFile = (file:File) => {
        setFiles(files.filter(x => x !== file));
    };

    const onUploadFiles = async () => {
        const folder = searchParams.get('folder');
        const institutionId = parseInt(params.institutionId!);
        const fundId = parseInt(params.fundId!);

        if(folder && !uploading) {
            try {
                if(institutionId && !fundId) {
                    setUploading(true);
                    await applicantHttpService.institutionUpload({
                        caseSlug: params.slug!,
                        folder: folder,
                        institutionId: institutionId
                    }, files);
                } else if(institutionId && fundId) {
                    setUploading(true);
                    await applicantHttpService.fundUpload({
                        caseSlug: params.slug!,
                        folder: folder,
                        institutionId: institutionId,
                        fundId: fundId
                    }, files);
                }
            } catch {
            } finally {
                setUploading(false);
                setShowFileUpload(false);
                setFiles([]);
                window.dispatchEvent(new CustomEvent('refresh-files'));
            }
        }
    };

    useEffect(() => {
        const paths = location.pathname.split('/');
        setCurrentTab(paths[3]);
        setCurrentCase(appStore.findCase(params.slug!));

        switch (paths[3]) {
            case 'investor-updates': {
                setCurrentTabName('Investor Updates');
                break;
            }
            case 'news': {
                setCurrentTabName('News');
                break;
            }
            case 'loss-report': {
                setCurrentTabName('Loss Report');
                break;
            }
            case 'claimant-details': {
                setCurrentTabName('Claimant Details');
                break;
            }
        }

        //Can we upload files here?
        setAllowUploadFiles(false);
        if(location.pathname.includes('files') && (params.institutionId || params.fundId)) {
            const allowedFolderNames = ["evidence","legal documents","transaction data"];
            const folder = searchParams.get('folder');

            if(folder) {
                let firstFolderName = folder;
                if(folder.includes('/')) {
                    firstFolderName = folder.split('/')[0];
                }
                setAllowUploadFiles(allowedFolderNames.some(x => x === firstFolderName.toLowerCase().trim()));
            }
            
        }
    }, [params.slug, params.institutionId, params.fundId, appStore, location, searchParams]);

    useEffect(() => {
        if (currentCase) {
            const items = new Array<IBreadcrumbItem>();
            items.push({ label: 'Dashboard', url: '/dashboard/latest-news' });
            items.push({ label: currentCase!.name, url: `/cases/${currentCase!.slug}/news` });
            items.push({ label: currentTabName, url: `/cases/${currentCase!.slug}/${currentTab}` });

            if(params.institutionId) {
                if(params.fundId) {
                    items.push({ label: appStore.currentApplicant?.name || '', url: `/cases/${currentCase!.slug}/${currentTab}/${params.institutionId}/view/funds/${params.fundId}/view/index` });
                    if(location.pathname.endsWith('index')) {
                        items.push({ label: 'Details', url: `/cases/${currentCase!.slug}/${currentTab}/${params.institutionId}/view/funds/${params.fundId}/view/index` });
                    } else if(location.pathname.endsWith('files')) {
                        items.push({ label: 'Files', url: `/cases/${currentCase!.slug}/${currentTab}/${params.institutionId}/view/funds/${params.fundId}/view/files` });
                    }
                } else {
                    if(location.pathname.endsWith('funds')) {
                        items.push({ label: 'Funds', url: `/cases/${currentCase!.slug}/${currentTab}/${params.institutionId}/view/funds` });
                    } else if(location.pathname.endsWith('files')) {
                        items.push({ label: 'Files', url: `/cases/${currentCase!.slug}/${currentTab}/${params.institutionId}/view/files` });
                    }
                }
            }


            setBreadcrumbItems(items);
        } else {
            setBreadcrumbItems([]);
        }
    }, [currentCase, currentTab, currentTabName, location.pathname, params, appStore.currentApplicant]);

    return (
        <>
            <EmailCaseManagerModal
                show={emailCaseManager}
                caseSlug={currentCase?.slug}
                caseManagerName="Alice Rees"
                onClose={() => {
                    setEmailCaseManager(false);
                }}
            />


            <button className="btn btn-back large display-in-mobile mb-l"></button>
           

            <Breadcrumbs items={breadcrumbItems} />

            <div className="spacing-l"></div>

            <div className="flex justify-right">

                {allowUploadFiles && (
                    <button className="btn tertiary medium" onClick={() => setShowFileUpload(true)}>
                        Upload file
                        <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 8.76938L7 4.61553M7 4.61553L8.84615 6.46169M7 4.61553L5.15385 6.46169M3.76923 10.6155C2.23983 10.6155 1 9.37571 1 7.8463C1 6.61988 1.79726 5.57966 2.90179 5.21565C2.86526 5.0212 2.84615 4.8206 2.84615 4.61553C2.84615 2.83123 4.29262 1.38477 6.07692 1.38477C7.57306 1.38477 8.83168 2.40175 9.19918 3.78215C9.37871 3.72392 9.5703 3.69246 9.76923 3.69246C10.7888 3.69246 11.6154 4.51901 11.6154 5.53861C11.6154 5.75285 11.5789 5.95857 11.5118 6.14988C12.3817 6.48043 13 7.32193 13 8.30784C13 9.58235 11.9668 10.6155 10.6923 10.6155H3.76923Z" stroke="#01065B" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                )}

                <button className="btn primary medium" onClick={() => setEmailCaseManager(true)} >
                    Email case manager
                    <Icon name="envelope-closed" />
                </button>
            </div >

            <div className="spacing-l"></div>

            <div className="flex chips-container">
                <Link className={clsx('chips', { 'active': currentTab === 'news' })} to={`/cases/${currentCase?.slug}/news`}>News</Link>
                {auth.userCases?.some(x => x.slug === currentCase?.slug) && (
                    <Link className={clsx('chips', { 'active': currentTab === 'investor-updates' })} to={`/cases/${currentCase?.slug}/investor-updates`}>Investor updates</Link>
                )}
                {auth.userCases?.some(x => x.slug === currentCase?.slug) && (<>
                    <Link className={clsx('chips', { 'active': currentTab === 'loss-report' })} to={`/cases/${currentCase?.slug}/loss-report`}>Loss Report</Link>
                    <Link className={clsx('chips', { 'active': currentTab === 'claimant-details' })} to={`/cases/${currentCase?.slug}/claimant-details`}>Claimant details</Link>
                </>)}
                {/* <Link className="chips unread active" to={`/cases/${myCase?.slug}/claimant-details`}>Claimant details</Link> */}
            </div>

            {currentTab === 'loss-report' && (
                <div className="flex justify-right">
                    <DropdownMenu selectedId={totalType}
                        items={[{ name: 'All totals', id: 'all' }, { name: 'Active totals', id: 'active' }, { name: 'Signed totals', id: 'signed' }]}
                        onItemSelected={item => {
                            setTotalType(item.id.toString());
                        }}
                    />
                </div>
            )}

            {showFileUpload && (
                <Modal
                    title="Upload Files"
                    onClose={() => {setShowFileUpload(false)}}>
                    <FileUploader 
                    
                        onUpload={(uploadedFiles: Array<File>) => {
                            setFiles([
                                ...files,
                                ...uploadedFiles
                            ]);
                        }}/>
                    <div className="file-upload-items">
                            {files.map((file, idx) => <FileUploadItem
                                key={idx}
                                name={file.name}
                                size={file.size}
                                file={file}
                                onDelete={(file) => { removeFile(file); }}
                            />)}
                        </div>

                        <div className="flex justify-right">
                            <button className="btn primary medium" onClick={() => onUploadFiles()}>Upload</button>
                            <button className="btn medium" onClick={() => setShowFileUpload(false)}>Close</button>
                        </div>
                </Modal>
            )}


            <Outlet context={{ currentTab, totalType }} />
        </>
    );
}